import { GenderEnum } from 'types/generated/client';
import { ALL_TENNIS_SYSTEM_IDS, TENNIS_SYSTEMS } from './systems';
import { TennisConversionResult, TennisConvertOptions, TennisRatingSystem } from './types';

/**
 * Anchor table for converting between tennis rating systems.
 * Each row is one "skill anchor" — a roughly equivalent point across systems.
 *
 * NTRP↔UTR anchors are reconciled with the in-product convertNtrpToUtr formula
 * in apps/bounce/src/utils/shared/tennis/convertNtrpToUtr.ts, which uses a
 * three-band multiplier (1.8 / 2.0 / 2.2) and a -0.5 NTRP factor for women.
 * Low-end anchors are flattened against published USTA/UTR guidance because the
 * raw formula overshoots below NTRP 3.5.
 *
 * WTN/ITN anchors come from each federation’s published cross-reference and
 * are inverted scales (lower = stronger).
 */
const ANCHOR_TABLE: Array<Record<TennisRatingSystem, number>> = [
  { ntrp: 1.0, dynamic_ntrp: 1.0, utr: 1.0, wtn: 40.0, itn: 10.3, self_rated: 1.0 },
  { ntrp: 1.5, dynamic_ntrp: 1.5, utr: 1.0, wtn: 38.0, itn: 10.2, self_rated: 1.5 },
  { ntrp: 2.0, dynamic_ntrp: 2.0, utr: 1.5, wtn: 34.0, itn: 10.1, self_rated: 2.0 },
  { ntrp: 2.5, dynamic_ntrp: 2.5, utr: 2.5, wtn: 28.0, itn: 9.0, self_rated: 2.5 },
  { ntrp: 3.0, dynamic_ntrp: 3.0, utr: 3.5, wtn: 22.0, itn: 8.0, self_rated: 3.0 },
  { ntrp: 3.5, dynamic_ntrp: 3.5, utr: 5.0, wtn: 17.0, itn: 7.0, self_rated: 3.5 },
  { ntrp: 4.0, dynamic_ntrp: 4.0, utr: 7.0, wtn: 12.0, itn: 6.0, self_rated: 4.0 },
  { ntrp: 4.5, dynamic_ntrp: 4.5, utr: 9.0, wtn: 8.0, itn: 5.0, self_rated: 4.5 },
  { ntrp: 5.0, dynamic_ntrp: 5.0, utr: 11.0, wtn: 5.0, itn: 4.0, self_rated: 5.0 },
  { ntrp: 5.5, dynamic_ntrp: 5.5, utr: 12.0, wtn: 3.0, itn: 3.0, self_rated: 5.5 },
  { ntrp: 6.0, dynamic_ntrp: 6.0, utr: 13.5, wtn: 2.0, itn: 2.0, self_rated: 6.0 },
  { ntrp: 6.5, dynamic_ntrp: 6.5, utr: 14.5, wtn: 1.5, itn: 1.5, self_rated: 7.0 },
  { ntrp: 7.0, dynamic_ntrp: 7.0, utr: 16.5, wtn: 1.0, itn: 1.0, self_rated: 7.0 },
];

/**
 * Per-system noise/uncertainty in the system’s own units. NTRP/self_rated and
 * the legacy ITN are loosest; algorithmic systems with the most data (UTR,
 * dynamic NTRP, WTN) are tightest.
 */
const SYSTEM_NOISE: Record<TennisRatingSystem, number> = {
  utr: 0.5,
  ntrp: 0.5,
  dynamic_ntrp: 0.2,
  wtn: 2.0,
  itn: 0.5,
  self_rated: 0.5,
};

/**
 * Pairs that share a scale by design — tighter cross-system uncertainty.
 */
const isTightlyAlignedPair = (a: TennisRatingSystem, b: TennisRatingSystem): boolean => {
  const aligned = new Set<string>([
    'ntrp|dynamic_ntrp',
    'dynamic_ntrp|ntrp',
    'ntrp|self_rated',
    'self_rated|ntrp',
    'dynamic_ntrp|self_rated',
    'self_rated|dynamic_ntrp',
  ]);
  return aligned.has(`${a}|${b}`);
};

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/**
 * Apply the gender adjustment from the in-product convertNtrpToUtr function
 * before looking up anchors. Only meaningful for the NTRP-family systems.
 * Mirrors apps/bounce/src/utils/shared/tennis/convertNtrpToUtr.ts.
 */
const applyNtrpGenderAdjustment = (
  value: number,
  system: TennisRatingSystem,
  gender: GenderEnum | null | undefined,
): number => {
  if (gender == null) return value;
  const isNtrpFamily = system === 'ntrp' || system === 'dynamic_ntrp' || system === 'self_rated';
  if (!isNtrpFamily) return value;

  if (gender === GenderEnum.Female) {
    return value - 0.5;
  }
  if (gender === GenderEnum.Male) {
    return value;
  }
  return value - 0.25;
};

/**
 * Find the anchor row index where `value` in `system` sits between two
 * adjacent anchor values, plus the interpolation factor t in [0, 1]. Handles
 * inverted scales (WTN, ITN) where higher index = lower number.
 */
const findAnchorPosition = (
  value: number,
  system: TennisRatingSystem,
): { lowIndex: number; highIndex: number; t: number } => {
  const def = TENNIS_SYSTEMS[system];
  const lastIndex = ANCHOR_TABLE.length - 1;

  for (let i = 0; i < lastIndex; i++) {
    const a = ANCHOR_TABLE[i][system];
    const b = ANCHOR_TABLE[i + 1][system];
    const low = Math.min(a, b);
    const high = Math.max(a, b);
    if (value >= low && value <= high) {
      const span = b - a;
      const t = span === 0 ? 0 : (value - a) / span;
      return { lowIndex: i, highIndex: i + 1, t };
    }
  }

  // Off the ends.
  if (def.isInverted) {
    if (value > ANCHOR_TABLE[0][system]) {
      return { lowIndex: 0, highIndex: 1, t: 0 };
    }
    return { lowIndex: lastIndex - 1, highIndex: lastIndex, t: 1 };
  }
  if (value < ANCHOR_TABLE[0][system]) {
    return { lowIndex: 0, highIndex: 1, t: 0 };
  }
  return { lowIndex: lastIndex - 1, highIndex: lastIndex, t: 1 };
};

const roundForSystem = (value: number, system: TennisRatingSystem): number => {
  const def = TENNIS_SYSTEMS[system];
  if (def.step >= 0.5) return Math.round(value * 2) / 2;
  if (def.step >= 0.1) return Math.round(value * 10) / 10;
  return Math.round(value * 100) / 100;
};

export const convertTennisRating = (
  inputValue: number,
  fromSystem: TennisRatingSystem,
  options: TennisConvertOptions = {},
): TennisConversionResult[] => {
  const fromDef = TENNIS_SYSTEMS[fromSystem];
  const adjustedInput = applyNtrpGenderAdjustment(inputValue, fromSystem, options.gender);
  const clampedInput = clamp(adjustedInput, fromDef.scaleMin, fromDef.scaleMax);
  const { lowIndex, highIndex, t } = findAnchorPosition(clampedInput, fromSystem);

  return ALL_TENNIS_SYSTEM_IDS.map((targetSystem) => {
    const lowTarget = ANCHOR_TABLE[lowIndex][targetSystem];
    const highTarget = ANCHOR_TABLE[highIndex][targetSystem];
    const interpolated = lerp(lowTarget, highTarget, t);
    const targetDef = TENNIS_SYSTEMS[targetSystem];

    const rounded = roundForSystem(
      clamp(interpolated, targetDef.scaleMin, targetDef.scaleMax),
      targetSystem,
    );

    let toleranceMultiplier = 1;
    if (targetSystem === fromSystem) {
      toleranceMultiplier = 0;
    } else if (isTightlyAlignedPair(fromSystem, targetSystem)) {
      toleranceMultiplier = 0.5;
    }
    const tolerance = (SYSTEM_NOISE[fromSystem] + SYSTEM_NOISE[targetSystem]) * toleranceMultiplier;

    const rangeLow = roundForSystem(
      clamp(interpolated - tolerance, targetDef.scaleMin, targetDef.scaleMax),
      targetSystem,
    );
    const rangeHigh = roundForSystem(
      clamp(interpolated + tolerance, targetDef.scaleMin, targetDef.scaleMax),
      targetSystem,
    );

    return {
      system: targetSystem,
      value: rounded,
      rangeLow: Math.min(rangeLow, rangeHigh),
      rangeHigh: Math.max(rangeLow, rangeHigh),
    };
  });
};

export const formatTennisRatingValue = (value: number, system: TennisRatingSystem): string => {
  const def = TENNIS_SYSTEMS[system];
  if (def.step >= 0.5) return value.toFixed(1);
  if (def.step >= 0.1) return value.toFixed(1);
  return value.toFixed(2);
};

export const formatTennisRatingRange = (
  low: number,
  high: number,
  system: TennisRatingSystem,
): string => {
  if (low === high) return formatTennisRatingValue(low, system);
  return `${formatTennisRatingValue(low, system)}–${formatTennisRatingValue(high, system)}`;
};
